<template>
  <v-text-field
    :id="id"
    v-model="fieldValue"
    :label="label"
    :type="type"
    :variant="variant"
    :placeholder="placeholder"
    :name="name"
    :autofocus="autofocus"
    :maxlength="maxlength"
    :error-messages="errors"
    :rules="rules"
    :append-inner-icon="appendIcon"
    :readonly="readonly"
    :step="step"
    :disabled="disabled"
    :rounded="rounded"
    :density="density"
    :debounce="debounceDelay"
    @blur="blur"
    @click:append-inner="clickAppend"
    @update:model-value="onUpdate($event)"
  />
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import debounce from "lodash/debounce";

interface Props {
  id?: string;
  name: string;
  label?: string;
  type?:
    | "text"
    | "password"
    | "textarea"
    | "email"
    | "search"
    | "tel"
    | "file"
    | "number"
    | "url"
    | "time"
    | "date"
    | undefined;
  variant?:
    | "outlined"
    | "plain"
    | "underlined"
    | "filled"
    | "solo"
    | "solo-inverted"
    | "solo-filled";
  placeholder?: string;
  maxlength?: string;
  appendIcon?: string;
  autofocus?: boolean;
  rules?: ((value: string) => boolean | string)[];
  readonly?: boolean;
  disabled?: boolean;
  rounded?: string;
  step?: string;
  density?: "default" | "comfortable" | "compact";
  allowedOnlyAsciiChars?: boolean;
  blur?: () => void;
  clickAppend?: () => void;
  debounceDelay?: number;
}

// Define the props with default values
const props = withDefaults(defineProps<Props>(), {
  id: "",
  variant: "underlined",
  label: "",
  type: "text",
  density: "comfortable",
  placeholder: "",
  maxlength: "191",
  appendIcon: "",
  allowedOnlyAsciiChars: false,
  rounded: "0",
  rules: () => [],
  clickAppend: () => {},
  blur: () => {},
  step: "",
  debounceDelay: 0,
});

const emit = defineEmits<(e: "on-update", value: string) => void>();

const debouncedUpdate = debounce((value: string) => {
  emit("on-update", value);
}, props.debounceDelay);

// Initialize field using vee-validate
const {
  value: fieldValue,
  errorMessage: errors,
  setValue,
} = useField<string>(props.name);

const onUpdate = (event: string) => {
  const cleanedValue = props.allowedOnlyAsciiChars
    ? fieldValue.value.replace(/[^\x20-\x7F]+/g, "")
    : fieldValue.value;

  if (props.allowedOnlyAsciiChars) {
    setValue(cleanedValue);
  } else {
    setValue(event);
  }

  debouncedUpdate(cleanedValue);
  emit("on-update", cleanedValue);
};
</script>
